import React, { useState } from "react";
import { Button, Link } from "@heroui/react";
import { LazyMotion, domAnimation, m } from "framer-motion";
import {
  ArrowDown,
  ArrowRight,
  Check,
  Factory,
  GitBranch,
  Headphones,
  Layers,
  Sparkles,
  Target,
  Wand2,
  Workflow,
} from "lucide-react";
import { useLocation } from "react-router-dom";

import BasicNavbar from "../BasicNavbar";
import { TargetIndustriesCards } from "../TargetIndustriesSection";
import FooterCTASection from "../../../components/FooterCTASection";
import SiteFooter from "../../../components/SiteFooter";
import { buildSignupHref, useTrialSignupHref } from "../../../lib/signupUrl";
import {
  agentCapabilities,
  builderCapabilities,
  clawCapabilities,
  raasPromises,
  raasRows,
  roleCards,
} from "./chat-agent-product-data";

const viewport = { once: true, margin: "-80px" } as const;
const ease = [0.22, 1, 0.36, 1] as const;

/** 与首页 Hero「免费试用」按钮一致 */
const MODULE_TRIAL_CTA_CLASS =
  "bg-default-foreground text-small text-background h-10 px-[16px] py-[10px] leading-5 font-medium transition-[background-color,color,box-shadow] duration-300 ease-out hover:bg-default-700 hover:text-background hover:shadow-lg hover:shadow-black/35";

/** 产品页：仅「在前线作战的 AI 销冠」等指定短语使用 hero 渐变 */
const PRODUCT_PAGE_ACCENT_PHRASE = "bg-hero-section-title bg-clip-text text-transparent";

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-4 inline-flex w-fit items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-xs font-medium text-default-700 backdrop-blur-md">
      {children}
    </div>
  );
}

function Blockquote({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <blockquote
      className={`border-l-2 border-default-200 text-default-400 italic leading-relaxed ${className ?? ""}`}
    >
      {children}
    </blockquote>
  );
}

/** 与拓扑 SVG 连线一致的图例线段样式（颜色继承 text-default-500） */
const TOPO_LINE = {
  builderAgent: {
    strokeWidth: 2,
    strokeDasharray: "8 10",
  },
  agentClaw: {
    strokeWidth: 2,
    strokeDasharray: undefined as string | undefined,
  },
} as const;

function TopologyLegendLine({
  variant,
}: {
  variant: keyof typeof TOPO_LINE;
}) {
  const line = TOPO_LINE[variant];
  return (
    <svg
      width="48"
      height="12"
      viewBox="0 0 48 12"
      className="shrink-0 text-default-400"
      aria-hidden
    >
      <line
        x1="0"
        y1="6"
        x2="40"
        y2="6"
        stroke="currentColor"
        strokeWidth={line.strokeWidth}
        {...(line.strokeDasharray ? { strokeDasharray: line.strokeDasharray } : {})}
        opacity={variant === "builderAgent" ? 0.85 : 0.9}
      />
      <polygon fill="currentColor" points="40,3 47,6 40,9" />
    </svg>
  );
}

function TopologyLegend() {
  return (
    <div
      className="mt-6 grid w-full grid-cols-2 gap-4 sm:gap-6"
      role="list"
      aria-label="拓扑图图例"
    >
      <div className="flex min-w-0 items-center gap-3" role="listitem">
        <TopologyLegendLine variant="builderAgent" />
        <p className="min-w-0 flex-1 text-left text-sm leading-relaxed text-default-500">
          <span className="text-default-500">Builder</span>{" "}
          <span className="font-medium text-foreground">生成</span>{" "}
          <span className="font-medium text-foreground">Agent</span>
          <span className="text-default-500">（一次性 / 按需迭代）</span>
        </p>
      </div>

      <div className="flex min-w-0 items-center gap-3" role="listitem">
        <TopologyLegendLine variant="agentClaw" />
        <p className="min-w-0 flex-1 text-left text-sm leading-relaxed text-default-500">
          <span className="text-default-500">Agent</span>{" "}
          <span className="font-medium text-foreground">双向调用</span>{" "}
          <span className="font-medium text-foreground">Claw</span>
          <span className="text-default-500">（前线作战实时触达 + 数据回流）</span>
        </p>
      </div>
    </div>
  );
}

const TOPO_NODE_R = { builder: 58, claw: 58, agent: 64 } as const;

/** 圆上朝向目标（或来源）的边缘点；angleOffset 顺时针微调（SVG y 向下，正值更靠底部） */
function circleEdgePoint(
  cx: number,
  cy: number,
  r: number,
  tx: number,
  ty: number,
  mode: "exit" | "enter",
  angleOffset = 0,
) {
  const dx = tx - cx;
  const dy = ty - cy;
  let angle = Math.atan2(dy, dx);
  if (mode === "enter") angle += Math.PI;
  angle += angleOffset;
  return { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) };
}

type TopoPt = { x: number; y: number };
type TopoCubic = { p0: TopoPt; p1: TopoPt; p2: TopoPt; p3: TopoPt };

function topoFmt(n: number) {
  return Number.isFinite(n) ? `${Math.round(n * 100) / 100}` : "0";
}

function lerpPt(a: TopoPt, b: TopoPt, t: number): TopoPt {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}

function cubicPt(t: number, p0: TopoPt, p1: TopoPt, p2: TopoPt, p3: TopoPt): TopoPt {
  const u = 1 - t;
  const u2 = u * u;
  const u3 = u2 * u;
  const t2 = t * t;
  const t3 = t2 * t;
  return {
    x: u3 * p0.x + 3 * u2 * t * p1.x + 3 * u * t2 * p2.x + t3 * p3.x,
    y: u3 * p0.y + 3 * u2 * t * p1.y + 3 * u * t2 * p2.y + t3 * p3.y,
  };
}

/** 弧线上某参数位置的点（t=0.5 为曲线中点） */
function cubicPointAt(cubic: TopoCubic, t = 0.5): TopoPt {
  return cubicPt(t, cubic.p0, cubic.p1, cubic.p2, cubic.p3);
}

function subdivideCubic(
  p0: TopoPt,
  p1: TopoPt,
  p2: TopoPt,
  p3: TopoPt,
  t: number,
): TopoCubic {
  const p01 = lerpPt(p0, p1, t);
  const p12 = lerpPt(p1, p2, t);
  const p23 = lerpPt(p2, p3, t);
  const p012 = lerpPt(p01, p12, t);
  const p123 = lerpPt(p12, p23, t);
  return { p0, p1: p01, p2: p012, p3: lerpPt(p012, p123, t) };
}

/** 曲线首次进入目标圆时的参数 t（从圆外穿入圆） */
function findCircleCrossingT(
  p0: TopoPt,
  p1: TopoPt,
  p2: TopoPt,
  p3: TopoPt,
  cx: number,
  cy: number,
  r: number,
) {
  let prevT = 0;
  let prevDist = Math.hypot(p0.x - cx, p0.y - cy) - r;

  for (let i = 1; i <= 120; i++) {
    const t = i / 120;
    const pt = cubicPt(t, p0, p1, p2, p3);
    const dist = Math.hypot(pt.x - cx, pt.y - cy) - r;
    if (dist <= 0 && prevDist > 0) {
      let lo = prevT;
      let hi = t;
      for (let j = 0; j < 24; j++) {
        const mid = (lo + hi) / 2;
        const pm = cubicPt(mid, p0, p1, p2, p3);
        const dm = Math.hypot(pm.x - cx, pm.y - cy) - r;
        if (dm > 0) lo = mid;
        else hi = mid;
      }
      return (lo + hi) / 2;
    }
    prevT = t;
    prevDist = dist;
  }
  return 1;
}

/**
 * 弧线：圆缘起止；若指定 trim 圆则在首次穿入该圆处截断（避免弧线穿过目标圆）
 * bulge：垂直于弦的偏移，正=向右 bulge
 */
function edgeArcPath(
  sx: number,
  sy: number,
  ex: number,
  ey: number,
  bulge: number,
  trim?: { cx: number; cy: number; r: number },
): { d: string; end: TopoPt; cubic: TopoCubic } {
  const p0: TopoPt = { x: sx, y: sy };
  const p3: TopoPt = { x: ex, y: ey };
  const dx = ex - sx;
  const dy = ey - sy;
  const len = Math.hypot(dx, dy) || 1;
  const px = (-dy / len) * bulge;
  const py = (dx / len) * bulge;
  const p1: TopoPt = { x: sx + dx * 0.28 + px, y: sy + dy * 0.28 + py };
  const p2: TopoPt = { x: sx + dx * 0.72 + px, y: sy + dy * 0.72 + py };

  let use: TopoCubic = { p0, p1, p2, p3 };
  if (trim) {
    const tHit = findCircleCrossingT(p0, p1, p2, p3, trim.cx, trim.cy, trim.r);
    if (Number.isFinite(tHit) && tHit > 0.001 && tHit < 0.998) {
      use = subdivideCubic(p0, p1, p2, p3, tHit);
    }
  }

  return {
    d: `M ${topoFmt(use.p0.x)} ${topoFmt(use.p0.y)} C ${topoFmt(use.p1.x)} ${topoFmt(use.p1.y)}, ${topoFmt(use.p2.x)} ${topoFmt(use.p2.y)}, ${topoFmt(use.p3.x)} ${topoFmt(use.p3.y)}`,
    end: use.p3,
    cubic: use,
  };
}

function TopoEdgeLabel({
  x,
  y,
  children,
  tone,
}: {
  x: number;
  y: number;
  children: string;
  tone: "builder" | "claw";
}) {
  const w = children.length * 11 + 20;
  const h = 22;
  return (
    <g className="text-default-500" transform={`translate(${x},${y})`}>
      <rect
        className="fill-background/90 stroke-default-500/50"
        height={h}
        rx={h / 2}
        strokeWidth={1}
        width={w}
        x={-w / 2}
        y={-h / 2}
      />
      <text
        className="fill-default-700"
        dominantBaseline="middle"
        fontSize={11}
        textAnchor="middle"
        y={1}
      >
        {children}
      </text>
    </g>
  );
}

type TopologyNodeProps = {
  cx: number;
  cy: number;
  emoji: string;
  name: string;
  variant?: "agent";
  glow?: string;
  large?: boolean;
};

function TopologyNode({
  cx,
  cy,
  emoji,
  name,
  variant,
  glow,
  large,
}: TopologyNodeProps) {
  const r = large ? TOPO_NODE_R.agent : TOPO_NODE_R.builder;
  const emojiY = large ? -8 : -6;
  const nameY = large ? 22 : 20;
  const isAgent = variant === "agent";

  return (
    <g
      className={isAgent ? undefined : "text-default-500"}
      transform={`translate(${cx},${cy})`}
    >
      {glow ? (
        <circle className="animate-pulse" fill={glow} opacity={0.35} r={r + 10} />
      ) : null}
      <circle
        className={isAgent ? undefined : "fill-default-100/50"}
        fill={isAgent ? "rgba(0, 183, 255, 0.3)" : undefined}
        r={r}
      />
      <circle
        className={isAgent ? undefined : "stroke-default-200"}
        fill="none"
        r={r - 1}
        stroke={isAgent ? "#00b7ff" : "currentColor"}
        strokeWidth={1}
      />
      <text fontSize={large ? 36 : 32} textAnchor="middle" y={emojiY}>
        {emoji}
      </text>
      <text
        fill="#fafafa"
        fontSize={large ? 19 : 17}
        style={{ fontWeight: 600 }}
        textAnchor="middle"
        y={nameY}
      >
        {name}
      </text>
    </g>
  );
}

function ProductTopologySvg() {
  const builder = { x: 130, y: 108 };
  const claw = { x: 470, y: 108 };
  const agent = { x: 300, y: 278 };

  /** 起点再靠圆底部一些（顺时针偏移） */
  const exitAngleBias = 0.52;

  const builderExit = circleEdgePoint(
    builder.x,
    builder.y,
    TOPO_NODE_R.builder,
    agent.x,
    agent.y,
    "exit",
    exitAngleBias,
  );
  const agentFromBuilder = circleEdgePoint(
    agent.x,
    agent.y,
    TOPO_NODE_R.agent,
    builder.x,
    builder.y,
    "enter",
  );
  /** 以 Agent 竖轴镜像，保证左右连线起终点对称 */
  const mirrorX = (x: number) => 2 * agent.x - x;
  const clawExit = { x: mirrorX(builderExit.x), y: builderExit.y };
  const agentFromClaw = { x: mirrorX(agentFromBuilder.x), y: agentFromBuilder.y };

  const agentTrim = { cx: agent.x, cy: agent.y, r: TOPO_NODE_R.agent };
  const arcBulge = 36;

  const builderArc = edgeArcPath(
    builderExit.x,
    builderExit.y,
    agentFromBuilder.x,
    agentFromBuilder.y,
    arcBulge,
    agentTrim,
  );
  const clawArc = edgeArcPath(
    clawExit.x,
    clawExit.y,
    agentFromClaw.x,
    agentFromClaw.y,
    -arcBulge,
    agentTrim,
  );

  const builderLabel = cubicPointAt(builderArc.cubic, 0.5);
  const clawLabel = cubicPointAt(clawArc.cubic, 0.5);

  return (
    <svg
      viewBox="0 0 600 360"
      className="h-auto w-full max-w-[600px] text-foreground"
      role="img"
      aria-label="3Chat 三位一体拓扑：Builder 生成 Agent，Claw 调用 Agent"
    >
      <defs>
        <marker
          id="productAgentArrowMutedEnd"
          markerHeight="8"
          markerWidth="8"
          markerUnits="userSpaceOnUse"
          orient="auto"
          refX="8"
          refY="4"
        >
          <polygon fill="currentColor" points="0 0, 8 4, 0 8" />
        </marker>
      </defs>

      <g className="text-default-500">
        {/* Builder → Agent（在 Agent 圆缘截断，箭头不穿过圆） */}
        <path
          d={builderArc.d}
          fill="none"
          stroke="currentColor"
          strokeDasharray="8 10"
          strokeLinecap="butt"
          strokeWidth="2.5"
          markerEnd="url(#productAgentArrowMutedEnd)"
          opacity="0.9"
        />
        <TopoEdgeLabel tone="builder" x={builderLabel.x} y={builderLabel.y}>
          孵化
        </TopoEdgeLabel>

        {/* Claw → Agent */}
        <path
          d={clawArc.d}
          fill="none"
          stroke="currentColor"
          strokeLinecap="butt"
          strokeWidth="2.5"
          markerEnd="url(#productAgentArrowMutedEnd)"
          opacity="0.95"
        />
        <TopoEdgeLabel tone="claw" x={clawLabel.x} y={clawLabel.y}>
          供弹
        </TopoEdgeLabel>

        <TopologyNode cx={builder.x} cy={builder.y} emoji="🏭" name="Builder" />
        <TopologyNode cx={claw.x} cy={claw.y} emoji="🦾" name="Claw" />
      </g>

      <TopologyNode
        cx={agent.x}
        cy={agent.y}
        emoji="🤖"
        name="Agent"
        variant="agent"
        glow="rgba(0, 183, 255, 0.45)"
        large
      />
    </svg>
  );
}

const MOCK_CHAT_CHANNELS = ["微信", "小红书", "抖音", "WhatsApp"] as const;

function MockChatPanel() {
  return (
    <div
      className="relative overflow-hidden rounded-3xl border border-default-100/50 bg-gradient-to-b from-default-200/50 via-default-200/15 to-background p-5 shadow-[0_24px_64px_-24px_rgba(0,0,0,0.65),0_0_48px_-16px_rgba(0,183,255,0.12)] sm:p-6"
      role="img"
      aria-label="销冠工作台对话示意"
    >
      <p className="text-default-400 text-xs font-medium tracking-wide">销冠工作台 · 实时</p>

      <div className="mt-3 flex flex-wrap gap-2">
        {MOCK_CHAT_CHANNELS.map((channel) => (
          <span
            key={channel}
            className={
              channel === "微信"
                ? "rounded-full bg-[#00b7ff] px-3 py-1 text-[11px] font-medium text-white shadow-[0_0_16px_rgba(0,183,255,0.35)]"
                : "rounded-full border border-default-100 bg-default-100 px-3 py-1 text-[11px] text-default-400"
            }
          >
            {channel}
          </span>
        ))}
      </div>

      <div className="mt-5 flex flex-col gap-3">
        <div className="max-w-[88%] self-start rounded-2xl rounded-tl-md bg-default-100 px-3.5 py-2.5 text-sm leading-relaxed text-default-700">
          [图片] 这件适合我吗？
        </div>

        <div className="max-w-[92%] self-end rounded-2xl rounded-tr-md bg-gradient-to-br from-[#00b7ff] to-[#0095d4] px-3.5 py-2.5 text-sm leading-relaxed text-white shadow-[0_8px_24px_-6px_rgba(0,183,255,0.45)]">
          您发的图我看了，这件偏修身款。结合您上次买的 M 码连衣裙建议选 M 偏紧 / L 更舒适 ✨
        </div>

        <div className="max-w-[72%] self-start rounded-2xl rounded-tl-md bg-default-100 px-3.5 py-2.5 text-sm leading-relaxed text-default-700">
          L 吧，发顺丰
        </div>

        <div className="max-w-[92%] self-end rounded-2xl rounded-tr-md bg-gradient-to-br from-[#00b7ff] to-[#0095d4] px-3.5 py-2.5 text-sm leading-relaxed text-white shadow-[0_8px_24px_-6px_rgba(0,183,255,0.45)]">
          已为您下单 L 码 + 顺丰快递，订单号 #SO20260513，可查物流：
          <span className="mt-0.5 block text-white/90">m.3chat.ai/o/xxx</span>
        </div>
      </div>

      <div className="mt-5 flex items-center justify-between gap-3 rounded-xl bg-emerald-500/90 px-3.5 py-2.5 text-xs font-medium text-white shadow-[0_4px_20px_-4px_rgba(16,185,129,0.45)]">
        <span>🎉 已成交 · ¥328 · 自动跟进物流</span>
        <span className="shrink-0 text-white/85">1 分 12 秒</span>
      </div>
    </div>
  );
}

const BUILDER_BRIEF =
  "我是做敏感肌护肤品的，主要在小红书和微信卖货，希望销冠专业、温柔、会推荐套装。";

const BUILDER_ARTIFACTS = [
  { icon: "🎭", label: "销冠人格", value: "温柔 · 专业 · 懂敏感肌" },
  { icon: "💬", label: "话术库", value: "迎客 / 答疑 / 推荐 / 追单" },
  { icon: "📚", label: "知识库", value: "30+ 成分 · 适用人群图谱" },
  { icon: "⚡", label: "工作流", value: "识别客资 → 推荐 → 闭单" },
  { icon: "🔗", label: "渠道", value: "小红书 + 微信已接入" },
] as const;

function MockBuilderPanel() {
  const [builderProgressDone, setBuilderProgressDone] = useState(false);

  return (
    <m.div
      className="relative overflow-hidden rounded-3xl border border-default-100/50 bg-gradient-to-b from-default-200/50 via-default-200/10 to-background p-5 shadow-[0_24px_64px_-24px_rgba(0,0,0,0.65),0_0_48px_-16px_rgba(0,183,255,0.1)] sm:p-6"
      role="img"
      aria-label="Builder 描述即交付流程示意"
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.55, ease }}
    >
      <m.div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.4]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)
          `,
          backgroundSize: "20px 20px",
        }}
      />
      <m.div
        aria-hidden
        className="pointer-events-none absolute -right-16 top-8 h-48 w-48 rounded-full bg-[#00b7ff]/15 blur-3xl"
      />


      <m.div
        className="relative rounded-2xl border border-default-200/50 bg-default-100/50 p-4 backdrop-blur-sm"
        initial={{ opacity: 0, x: -8 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.45, delay: 0.08, ease }}
      >
        <m.div className="mb-3 flex items-center gap-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-lg text-primary">
            <Wand2 className="h-3.5 w-3.5" aria-hidden />
          </span>
          <span className="text-default-500 text-xs font-medium">业务描述</span>
        </m.div>
        <p className="text-default-800 text-sm leading-relaxed">「{BUILDER_BRIEF}」</p>
      </m.div>

      <m.div
        aria-hidden
        className="relative mx-auto my-3 flex h-8 w-8 items-center justify-center"
      >
        <m.div className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-gradient-to-b from-primary/20 via-primary/60 to-primary/20" />
        <span className="relative rounded-full border border-primary/40 bg-background p-1">
          <ArrowDown className="h-3.5 w-3.5 text-primary" strokeWidth={2} />
        </span>
      </m.div>

      <m.div
        className="relative rounded-2xl border border-default-200/50 bg-gradient-to-br from-default-100/70 via-default-100/10 to-transparent backdrop-blur-sm p-4"
        initial={{ opacity: 0, x: 8 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.45, delay: 0.14, ease }}
      >
        <m.div
          aria-hidden
          className="relative mb-4 h-1.5 overflow-hidden rounded-full bg-default-100/50"
        >
          <m.div
            className="absolute inset-y-0 left-0 h-full rounded-full bg-gradient-to-r from-[#00b7ff]/60 via-[#00b7ff] to-[#00b7ff]/80"
            initial={{ width: "0%" }}
            whileInView={{ width: "100%" }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, delay: 0.2, ease }}
            onAnimationComplete={() => setBuilderProgressDone(true)}
          />
          <m.div
            aria-hidden
            className="pointer-events-none absolute inset-y-0 left-0 z-[1] h-full w-full rounded-full bg-gradient-to-r from-emerald-600 via-green-400 to-lime-300"
            initial={{ opacity: 0 }}
            animate={{ opacity: builderProgressDone ? 1 : 0 }}
            transition={{ duration: 0.55, ease }}
          />
        </m.div>
        <ul className="space-y-2">
          {BUILDER_ARTIFACTS.map((item, idx) => (
            <m.li
              key={item.label}
              className="flex items-center gap-2 rounded-xl border border-default-100/30 bg-foreground/10 px-3 py-2"
              initial={{ opacity: 0, y: 6 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35, delay: 0.22 + idx * 0.06, ease }}
            >
              <Check
                className="h-3.5 w-3.5 shrink-0 text-primary"
                strokeWidth={2.5}
                aria-hidden
              />
              <span className="shrink-0 text-sm leading-none" aria-hidden>
                {item.icon}
              </span>
              <span className="shrink-0 text-sm font-medium text-default-600">{item.label}</span>
              <span className="min-w-0 flex-1 truncate text-right text-sm font-medium text-default-800">
                {item.value}
              </span>
            </m.li>
          ))}
        </ul>
      </m.div>

      <m.div
        aria-hidden
        className="relative mx-auto my-3 flex h-8 w-8 items-center justify-center"
      >
        <m.div className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-gradient-to-b from-primary/30 to-[#00b7ff]" />
        <span className="relative rounded-full border border-[#00b7ff]/50 bg-background p-1">
          <ArrowDown className="h-3.5 w-3.5 text-[#00b7ff]" strokeWidth={2} />
        </span>
      </m.div>

      <m.div
        className="relative overflow-hidden rounded-2xl border border-success-200/30 bg-gradient-to-r from-success-200/20 to-success-100/10 p-4"
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.28, ease }}
      >
        <m.div
          aria-hidden
          className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-success-100/20 blur-2xl"
        />
        <m.div className="relative flex items-center gap-3">
          <m.div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-2xl ">
            🚀
          </m.div>
          <m.div className="min-w-0 flex-1">
            <m.div className="flex flex-wrap items-center gap-2">
              <p className="text-foreground text-sm font-semibold">敏感肌护肤销冠</p>
              <span className="rounded-full bg-emerald-500/90 px-2 py-0.5 text-[10px] font-medium text-white">
                已上岗
              </span>
            </m.div>
            <p className="text-default-500 mt-1 text-xs">
              用时 <span className="text-primary font-medium tabular-nums">2 分 47 秒</span> · 已开始接待
            </p>
          </m.div>
        </m.div>
      </m.div>
    </m.div>
  );
}

function MockClawRadial() {
  const nodes = [
    { label: "微信", pos: "top-0 left-1/2 -translate-x-1/2" },
    { label: "抖音", pos: "top-[18%] right-0" },
    { label: "小红书", pos: "bottom-[18%] right-0" },
    { label: "WhatsApp", pos: "bottom-0 left-1/2 -translate-x-1/2" },
    { label: "Shopify", pos: "bottom-[18%] left-0" },
    { label: "IG/TG", pos: "top-[18%] left-0" },
  ];
  return (
    <div className="relative mx-auto aspect-square w-full max-w-[340px]">
      <div className="absolute inset-6 rounded-full border border-dashed border-primary/50" />
      {nodes.map((n) => (
        <div
          key={n.label}
          className={`absolute ${n.pos} rounded-full border border-default-100/60 bg-default-100 px-3 py-2 text-[12px] text-foreground`}
        >
          {n.label}
        </div>
      ))}
      <div className="absolute left-1/2 top-1/2 flex h-24 w-24 -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center gap-0.5 rounded-full border-2 border-primary/80 px-2 text-center shadow-lg shadow-[0_0_24px_rgba(0,183,255,0.25)]">
        <span className="text-2xl leading-none" aria-hidden>
          🦾
        </span>
        <span className="text-foreground text-sm font-semibold tracking-wide pt-2">Claw</span>
      </div>
      <p className="absolute bottom-[-2rem] left-0 right-0 text-center text-[12px] text-default-500">
        20+ 社交电商渠道 · 原生双向集成
      </p>
    </div>
  );
}

export default function ChatAgentProductPage() {
  const location = useLocation();
  const trialHref = useTrialSignupHref("product-agent-page");
  const builderTrialHref = React.useMemo(
    () => buildSignupHref("product-agent-builder-cta", { search: location.search }),
    [location.search],
  );

  return (
    <div className="dark">
      <div
        className="bg-background relative flex min-h-screen min-h-dvh w-full flex-col overflow-x-hidden scroll-smooth"
        style={{ ["--navbar-height" as string]: "60px" } as React.CSSProperties}
      >
        <BasicNavbar />
        <main className="relative z-10 mx-auto mt-[calc(80px+var(--intl-notice-height,0px))] flex w-full max-w-6xl flex-1 flex-col px-6 pb-20 sm:px-8">
          <LazyMotion features={domAnimation}>
            {/* Hero */}
            <section
              id="hero"
              className="scroll-mt-[calc(96px+var(--intl-notice-height,0px))] grid gap-12 py-12 lg:grid-cols-2 lg:items-center lg:py-16"
            >
              <m.div
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, ease }}
                className="flex flex-col gap-6"
              >
                <Eyebrow>
                  <Layers className="h-3.5 w-3.5 text-primary" />
                  <span className="tracking-wide">产品体系 · Three-in-One</span>
                </Eyebrow>
                <h1 className="text-foreground text-[1.65rem] font-bold leading-[1.15] tracking-tight sm:text-4xl lg:text-[2.75rem]">
                  不是「客服系统」——
                  <span className="mt-2 block bg-hero-section-title bg-clip-text text-transparent">
                    是会赚钱的 AI 销冠团队。
                  </span>
                </h1>
                <p className="text-default-500 text-sm italic sm:text-base">
                  Agent + Builder + Claw · One unified sales force.
                </p>
                <p className="text-default-400 max-w-xl leading-7">
                  <span className="text-foreground font-medium">3Chat Agent</span> 在前线作战，{" "}
                  <span className="text-foreground font-medium">3Chat Builder</span> 3 分钟生成专属销冠，{" "}
                  <span className="text-foreground font-medium">3Chat Claw</span>{" "}
                  把销冠的「手脚」伸向 20+ 社交电商渠道。三位一体，构成一个{" "}
                  <span className="font-semibold text-foreground">Always-on 的成交系统。 </span> 
                </p>
                <div className="flex flex-wrap gap-3">
                  <Button
                    as={Link}
                    className="bg-default-foreground text-background font-medium"
                    href={trialHref}
                    radius="full"
                    endContent={<ArrowRight className="h-4 w-4" />}
                  >
                    免费试用
                  </Button>
                  <Button
                    radius="full"
                    variant="bordered"
                    className="border-default-200 border-1"
                    onPress={() => {
                      const reduceMotion =
                        typeof window !== "undefined" &&
                        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
                      document.getElementById("topo")?.scrollIntoView({
                        behavior: reduceMotion ? "auto" : "smooth",
                        block: "start",
                      });
                      const { pathname, search } = window.location;
                      window.history.replaceState(null, "", `${pathname}${search}#topo`);
                    }}
                  >
                    查看架构图
                  </Button>
                </div>
              </m.div>

              <m.div
                initial={{ opacity: 0, y: 22 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.08, ease }}
                className="relative"
              >
                <div className="pointer-events-none absolute -inset-6 rounded-2xl bg-[radial-gradient(ellipse_at_30%_20%,rgba(0,183,255,0.14),transparent_55%),radial-gradient(ellipse_at_80%_60%,rgba(139,58,255,0.12),transparent_50%)]" />
                <div className="relative overflow-hidden rounded-2xl backdrop-blur-sm">
                  <div className="flex items-start gap-4">
                    {/* <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/15 text-2xl">
                      🎯
                    </div> */}
                    <div>
                      <p className="text-default-400 mt-2 leading-relaxed">RaaS · Result as a Service</p>
                      <p className="text-foreground text-lg font-semibold">
                        我们交付的不是软件，而是每个月新增的成交订单
                      </p>
                    </div>
                  </div>
                  <div className="mt-6 overflow-hidden rounded-2xl border border-default-100/50">
                    <img
                      src="/images/product-matrix/3ChatAgent.png"
                      alt="3Chat Agent 产品示意"
                      className="h-auto w-full object-cover opacity-95"
                      loading="lazy"
                    />
                  </div>
                </div>
              </m.div>
            </section>

            {/* Topology */}
            <section
              id="topo"
              className="scroll-mt-[calc(96px+var(--intl-notice-height,0px))] border-t border-default-100/40 py-16 sm:py-20"
            >
              <m.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={viewport}
                transition={{ duration: 0.55, ease }}
                className="mx-auto max-w-3xl text-center"
              >
                <Eyebrow>
                  <GitBranch className="h-3.5 w-3.5 text-primary" />
                  <span className="tracking-wide">系统拓扑</span>
                </Eyebrow>
                <h2 className="text-foreground text-2xl font-bold tracking-tight sm:text-3xl lg:text-[2.1rem]">
                  一个销冠 · 一个生产线 · 一组手脚
                </h2>
                <p className="text-default-500 mx-auto mt-4 max-w-2xl leading-7">
                  三大模块协同运作，把「流量进来 → 订单成交」的全链路交给 AI
                </p>
              </m.div>

              <m.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={viewport}
                transition={{ duration: 0.6, delay: 0.05, ease }}
                className="mt-12 flex justify-center"
              >
                <ProductTopologySvg />
              </m.div>

              <TopologyLegend />

              <div className="mt-10 grid gap-4 sm:grid-cols-3">
                {roleCards.map((card, idx) => (
                  <m.div
                    key={card.name}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={viewport}
                    transition={{ duration: 0.5, delay: 0.06 * idx, ease }}
                    className="rounded-2xl border border-default-100/60 bg-default-100/15 p-5"
                  >
                    <p className="text-primary text-xs font-medium">{card.tag}</p>
                    <p className="text-foreground mt-1 font-semibold">{card.name}</p>
                    <p className="text-default-500 mt-3 text-sm leading-6">{card.body}</p>
                  </m.div>
                ))}
              </div>
            </section>

            {/* Module A Agent */}
            <section
              id="agent"
              className="scroll-mt-[calc(96px+var(--intl-notice-height,0px))] border-t border-default-100/40 py-16 sm:py-20"
            >
              <div className="grid gap-10 lg:grid-cols-2 lg:items-start">
                <m.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={viewport}
                  transition={{ duration: 0.55, ease }}
                >
                  <Eyebrow>
                    <Headphones className="h-3.5 w-3.5 text-primary" />
                    <span className="tracking-wide">Module A · 大脑</span>
                  </Eyebrow>
                  <h2 className="text-foreground text-2xl font-bold tracking-tight sm:text-3xl">
                    3Chat Agent —
                    <span className={`mt-1 block ${PRODUCT_PAGE_ACCENT_PHRASE}`}>
                      在前线作战的 AI 销冠
                    </span>
                  </h2>
                  <Blockquote className="mt-5">
                    「具备自主性、记忆、多模态理解和工具编排能力的 AI 销冠智能体」
                  </Blockquote>
                  <p className="text-default-500 mt-5 leading-7">
                    不是脚本式机器人，而是
                    <span className="text-foreground font-semibold">
                      能判断意图、能主动跟进、能记住每个客户、能调用业务系统完成交易
                    </span>
                    的销冠大脑。每一次对话，都是为成交而生。
                  </p>
                  <Button
                    as={Link}
                    className={`mt-6 ${MODULE_TRIAL_CTA_CLASS}`}
                    href={trialHref}
                    radius="full"
                  >
                    让 Agent 上岗
                  </Button>
                </m.div>
                <m.div
                  initial={{ opacity: 0, y: 22 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={viewport}
                  transition={{ duration: 0.58, delay: 0.06, ease }}
                >
                  <MockChatPanel />
                </m.div>
              </div>

              <div className="mt-12 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {agentCapabilities.map((cap, idx) => (
                  <m.div
                    key={cap.id}
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={viewport}
                    transition={{ duration: 0.45, delay: 0.04 * idx, ease }}
                    className="rounded-2xl border border-default-200/40 bg-default-100/40 p-4"
                  >
                    <p className="text-[11px] font-semibold tracking-wide text-primary">{cap.labelEn}</p>
                    <p className="text-foreground mt-1 font-medium">{cap.labelZh}</p>
                    <p className="text-default-500 mt-2 text-sm leading-6">{cap.description}</p>
                  </m.div>
                ))}
              </div>
            </section>

            {/* Module B Builder */}
            <section
              id="builder"
              className="scroll-mt-[calc(96px+var(--intl-notice-height,0px))] border-t border-default-100/40 py-16 sm:py-20"
            >
              <div className="grid gap-10 lg:grid-cols-2 lg:items-start">
                <m.div
                  initial={{ opacity: 0, y: 22 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={viewport}
                  transition={{ duration: 0.55, ease }}
                  className="order-2 lg:order-1"
                >
                  <MockBuilderPanel />
                </m.div>
                <m.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={viewport}
                  transition={{ duration: 0.55, delay: 0.05, ease }}
                  className="order-1 lg:order-2"
                >
                  <Eyebrow>
                    <Factory className="h-3.5 w-3.5 text-primary" />
                    <span className="tracking-wide">Module B · 生产线</span>
                  </Eyebrow>
                  <h2 className="text-foreground text-2xl font-bold tracking-tight sm:text-3xl">
                    3Chat Builder —
                    <span className="mt-1 block">
                      3 分钟，
                      <span className={PRODUCT_PAGE_ACCENT_PHRASE}>生成你的专属 AI 销冠</span>
                    </span>
                  </h2>
                  <Blockquote className="mt-5">
                    「行业 AI 销冠的生成器——描述即交付，无需代码」
                  </Blockquote>
                  <p className="text-default-500 mt-5 leading-7">
                    告诉 Builder 你做什么生意、卖什么产品、希望销冠是什么风格——它就能自动生成
                    <span className="text-foreground font-semibold">话术体系、工作流、知识库、跟进策略</span>
                    。从「想要」到「上岗」，只需 3 分钟。
                  </p>
                  <ul className="mt-6 space-y-4">
                    {builderCapabilities.map((b) => (
                      <li key={b.title} className="flex gap-3">
                        <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                        <div>
                          <p className="text-foreground font-medium">{b.title}</p>
                          <p className="text-default-500 mt-1 text-sm leading-6">{b.description}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                  <Button
                    as={Link}
                    className={`mt-6 ${MODULE_TRIAL_CTA_CLASS}`}
                    href={builderTrialHref}
                    radius="full"
                  >
                    立即生成销冠
                  </Button>
                </m.div>
              </div>
            </section>

            {/* Module C Claw */}
            <section
              id="claw"
              className="scroll-mt-[calc(96px+var(--intl-notice-height,0px))] border-t border-default-100/40 py-16 sm:py-20"
            >
              <div className="grid gap-10 lg:grid-cols-2 lg:items-start">
                <m.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={viewport}
                  transition={{ duration: 0.55, ease }}
                >
                  <Eyebrow>
                    <Workflow className="h-3.5 w-3.5 text-primary" />
                    <span className="tracking-wide">Module C · 手脚</span>
                  </Eyebrow>
                  <h2 className="text-foreground text-2xl font-bold tracking-tight sm:text-3xl">
                    3Chat Claw —
                    <span className="mt-1 block">
                      <span className={PRODUCT_PAGE_ACCENT_PHRASE}>全渠道</span>
                      触达 ×{" "}
                      <span className={PRODUCT_PAGE_ACCENT_PHRASE}>全场景</span>
                      成交
                    </span>
                  </h2>
                  <Blockquote className="mt-5">
                    「系统集成器与销冠的「手脚」——让 AI 真正能出手做事」
                  </Blockquote>
                  <p className="text-default-500 mt-5 leading-7">
                    销冠光会说不行，得能「出手」。Claw 把销冠的能力延伸到{" "}
                    <span className="text-foreground font-semibold">20+ 社交电商渠道 + 业务系统</span>
                    ，从接客到下单到物流到售后到复购，全场景闭环成交，
                    <span className="text-foreground font-semibold">不是回答问题，而是完成交易</span>。
                  </p>
                  <ul className="mt-6 space-y-4">
                    {clawCapabilities.map((c) => (
                      <li key={c.title} className="flex gap-3">
                        <Target className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                        <div>
                          <p className="text-foreground font-medium">{c.title}</p>
                          <p className="text-default-500 mt-1 text-sm leading-6">{c.description}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                  <Button
                    as={Link}
                    className={`mt-6 ${MODULE_TRIAL_CTA_CLASS}`}
                    href="https://claw.3chatai.com/"
                    isExternal
                    radius="full"
                  >
                    查看渠道清单
                  </Button>
                </m.div>
                <m.div
                  initial={{ opacity: 0, y: 22 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={viewport}
                  transition={{ duration: 0.58, delay: 0.06, ease }}
                  className="rounded-2xl border border-default-200/50 bg-gradient-to-b from-default-200/50 via-default-200/15 to-background p-6 pb-16"
                >
                  <MockClawRadial />
                </m.div>
              </div>
            </section>

            {/* Industry */}
            <section
              id="industry"
              className="scroll-mt-[calc(96px+var(--intl-notice-height,0px))] border-t border-default-100/40 py-16 sm:py-20"
            >
              <m.div
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={viewport}
                transition={{ duration: 0.55, ease }}
                className="mx-auto max-w-3xl text-center"
              >
                <Eyebrow>
                  <Layers className="h-3.5 w-3.5 text-primary" />
                  <span className="tracking-wide">行业方案</span>
                </Eyebrow>
                <h2 className="text-foreground text-2xl font-bold tracking-tight sm:text-3xl">
                  5 大行业，开箱即用
                </h2>
                <p className="text-default-500 mx-auto mt-4 max-w-2xl leading-7">
                  每个行业都有专属的销冠模板与最佳实践，无需从零搭建
                </p>
              </m.div>

              <div className="mx-auto mt-10 w-full max-w-[1200px] px-0 sm:px-0">
                <TargetIndustriesCards />
              </div>
            </section>

            {/* RaaS */}
            <section
              id="raas"
              className="scroll-mt-[calc(96px+var(--intl-notice-height,0px))] border-t border-default-100/40 py-16 sm:py-20"
            >
              <m.div
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={viewport}
                transition={{ duration: 0.55, ease }}
                className="mx-auto max-w-3xl text-center"
              >
                <Eyebrow>
                  <Target className="h-3.5 w-3.5 text-primary" />
                  <span className="tracking-wide">RaaS · 我们的承诺</span>
                </Eyebrow>
                <h2 className="text-foreground text-2xl font-bold tracking-tight sm:text-3xl">
                  不卖软件，只交付成果
                </h2>
                <p className="text-default-500 mx-auto mt-4 max-w-2xl leading-7">
                  传统 SaaS 卖你账号、卖你席位、卖你 Token——3Chat.ai 卖给你的是
                  每个月新增的成交订单
                </p>
              </m.div>

              <m.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={viewport}
                transition={{ duration: 0.55, delay: 0.05, ease }}
                className="mt-10 overflow-x-auto rounded-2xl border border-default-100/60"
              >
                <div className="grid min-w-[520px] grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)_minmax(0,1.1fr)] gap-0 text-sm">
                  <div className="bg-default-100/30 px-3 py-3 font-medium text-default-700 sm:px-4">
                    对比维度
                  </div>
                  <div className="bg-default-100/20 px-3 py-3 font-medium text-default-700 sm:px-4">
                    传统客服 SaaS
                  </div>
                  <div className="bg-primary/10 px-3 py-3 font-semibold text-primary sm:px-4">
                    3Chat.ai · RaaS
                  </div>
                  {raasRows.map((row) => (
                    <React.Fragment key={row.dimension}>
                      <div className="border-t border-default-100/50 px-3 py-3 text-default-700 sm:px-4">
                        {row.dimension}
                      </div>
                      <div className="border-t border-default-100/50 px-3 py-3 text-default-700 sm:px-4">
                        {row.traditional}
                      </div>
                      <div className="border-t border-default-100/50 bg-primary/5 px-3 py-3 font-medium text-primary sm:px-4">
                        {row.raas}
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </m.div>

              <div className="mt-8 grid gap-4 md:grid-cols-3">
                {raasPromises.map((p, idx) => (
                  <m.div
                    key={p.title}
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={viewport}
                    transition={{ duration: 0.48, delay: 0.06 * idx, ease }}
                    className="rounded-2xl border border-default-100/60 bg-default-100/10 p-5"
                  >
                    <p className="text-foreground font-semibold">{p.title}</p>
                    <p className="text-default-500 mt-2 text-sm leading-6">{p.description}</p>
                  </m.div>
                ))}
              </div>
              <p className="text-default-500 mt-4 text-center text-xs">
                「14 天产能保证」等商务承诺请以合同与法务确认版本为准。
              </p>
            </section>
          </LazyMotion>
        </main>

        <div
          id="cta"
          className="w-full scroll-mt-[calc(80px+var(--intl-notice-height,0px))]"
        >
          <FooterCTASection />
        </div>
        <SiteFooter className="mt-0" />
      </div>
    </div>
  );
}
